/**
 * Native Bridge Service
 * 处理Web应用与iOS/Android原生应用的通信
 */

import toast from "./toastService";

// 原生应用传递给Web的数据接口
export interface NativeInitData {
  xToken?: string;
  imageUrl?: string;
  imageUrls?: string[];
  userId?: string;
  userName?: string;
  otherData?: Record<string, unknown>;
}

// 全局状态存储
let nativeData: NativeInitData = {};
let isNativeApp = false;

/**
 * 检测当前环境是否在原生应用中
 */
export function isInNativeApp(): boolean {
  if (isNativeApp) {
    return true;
  }

  if (window.nativeBridge && typeof window.nativeBridge.postMessage === 'function') {
    console.log('isInNativeApp nativeBridge >>> ', window.nativeBridge)
    isNativeApp = true;
    return true;
  }

  // 检查 iOS WKWebView 的 messageHandlers
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeBridge) {
    console.log('isInNativeApp webkit >>> ', window.webkit.messageHandlers.nativeBridge)
    isNativeApp = true;
    return true;
  }

  // if (window.Android && typeof window.Android.postMessage === 'function') {
  //   isNativeApp = true;
  //   return true;
  // }

  // if (window.ArkTSBridge && typeof window.ArkTSBridge.postMessage === 'function') {
  //   isNativeApp = true;
  //   return true;
  // }

  return false;
}

/**
 * 获取原生应用传递的数据
 */
export function getNativeData(): NativeInitData {
  return { ...nativeData };
}

/**
 * 从URL参数解析原生数据
 * 支持格式: ?xToken=xxx&imageUrl=xxx&userId=xxx
 */
function parseUrlParams(): NativeInitData {
  const params = new URLSearchParams(window.location.search);
  const data: NativeInitData = {};
  // toast.info(`parseUrlParams: ${window.location.search}`)
  console.log(`parseUrlParams: ${window.location.search}`)
  // 解析xToken
  const token = params.get('xToken') || params.get('token');
  if (token) {
    data.xToken = token;
  }
  console.log(`parseUrlParams token: ${token}`)
  // 解析单个图片URL
  const imageUrl = params.get('imageUrl') || params.get('image');
  if (imageUrl) {
    data.imageUrl = imageUrl;
  }

  // 解析多个图片URL（逗号分隔）
  const imageUrlsParam = params.get('imageUrls') || params.get('images');
  if (imageUrlsParam) {
    data.imageUrls = imageUrlsParam.split(',').filter(url => url.trim());
  }

  // 解析用户信息
  const userId = params.get('userId');
  if (userId) {
    data.userId = userId;
  }

  const userName = params.get('userName');
  if (userName) {
    data.userName = userName;
  }
  return data;
}

/**
 * 从localStorage读取原生数据
 * 用于WebView中持久化数据
 */
function loadFromLocalStorage(): NativeInitData {
  try {
    const stored = localStorage.getItem('nativeInitData');
    if (stored) {
      const data = JSON.parse(stored) as NativeInitData;
      if (data.xToken || data.imageUrl || data.imageUrls) {
        return data;
      }
    }
  } catch (error) {
    console.warn('Failed to load from localStorage:', error);
  }
  return {};
}

/**
 * 初始化原生桥接
 * 在应用启动时调用
 */
export function initNativeBridge(): NativeInitData {
  // 优先从URL参数获取
  const urlData = parseUrlParams();
  // toast.info(`initNativeBridge: ${JSON.stringify(urlData)}`)
  // 如果URL参数没有数据，尝试从localStorage获取
  const localStorageData = loadFromLocalStorage();

  // 合并数据（URL参数优先级更高）
  nativeData = {
    ...localStorageData,
    ...urlData,
  };

  // 检查并更新原生应用状态
  isInNativeApp();

  console.log('Native Bridge initialized:', {
    isNativeApp,
    nativeData,
  });

  return nativeData;
}

/**
 * 设置原生数据（供原生应用调用）
 * iOS/Android可以通过注入JS调用此方法
 */
export function setNativeData(data: Partial<NativeInitData>): void {
  nativeData = { ...nativeData, ...data };

  // 保存到localStorage以便持久化
  try {
    localStorage.setItem('nativeInitData', JSON.stringify(nativeData));
  } catch (error) {
    console.warn('Failed to save to localStorage:', error);
  }

  console.log('Native data updated:', nativeData);
}

/**
 * 获取xToken
 */
export function getXToken(): string | undefined {
  return nativeData.xToken;
}

/**
 * 设置xToken
 */
export function setXToken(token: string): void {
  setNativeData({ xToken: token });
}

/**
 * 获取图片URL列表
 */
export function getImageUrls(): string[] {
  const urls: string[] = [];

  if (nativeData.imageUrl) {
    urls.push(nativeData.imageUrl);
  }

  if (nativeData.imageUrls && nativeData.imageUrls.length > 0) {
    urls.push(...nativeData.imageUrls);
  }

  return urls;
}

/**
 * 添加图片URL
 */
export function addImageUrl(url: string): void {
  if (!nativeData.imageUrls) {
    nativeData.imageUrls = [];
  }
  nativeData.imageUrls.push(url);
  setNativeData({ imageUrls: nativeData.imageUrls });
}

/**
 * 清除原生数据
 */
export function clearNativeData(): void {
  toast.info(`clearNativeData`)
  nativeData = {};
  isNativeApp = false;
  try {
    localStorage.removeItem('nativeInitData');
  } catch (error) {
    console.warn('Failed to clear localStorage:', error);
  }
}

/**
 * 向原生应用发送消息
 * type: 'doAction,updateMenus'
 * data: doAction: {action?: string, targetId?: number, targetData?: string}
 * data: updateMenus: { title: '我的列表', postData: { type: 'viewMyModels', data: {} } },
 */
export function postMessageToNative(type: string, data?: unknown): void {
  const message = { type, data, timestamp: Date.now() };

  if (window.nativeBridge && typeof window.nativeBridge.postMessage === 'function') {
    window.nativeBridge.postMessage(JSON.stringify(message));
    console.log('Post message to Android native:', message);
    return;
  }

  // 支持 iOS WKWebView 的 messageHandlers
  if (window.webkit && window.webkit.messageHandlers && window.webkit.messageHandlers.nativeBridge) {
    window.webkit.messageHandlers.nativeBridge.postMessage(JSON.stringify(message));
    console.log('Post message to iOS native:', message);
    return;
  }

  // if (window.Android && typeof window.Android.postMessage === 'function') {
  //   window.Android.postMessage(JSON.stringify(message));
  //   console.log('Post message to Android native:', message);
  //   return;
  // }

  // if (window.ArkTSBridge && typeof window.ArkTSBridge.postMessage === 'function') {
  //   window.ArkTSBridge.postMessage(JSON.stringify(message));
  //   console.log('Post message to HarmonyOS native:', message);
  //   return;
  // }

  console.log('Post message to native (simulated):', message);
}

/**
 * 监听原生应用发送的消息
 * 需要在原生应用中通过window注入数据
 */
export function listenToNativeMessages(): void {
  // 监听window上的自定义事件
  window.addEventListener('message', (event) => {
    // 验证消息来源
    if (event.origin !== window.location.origin) {
      console.warn('Message from untrusted origin:', event.origin);
      return;
    }

    const data = event.data;
    if (data && data.type) {
      handleNativeMessage(data);
    }
  });

  // 监听原生应用通过window注入的数据
  // iOS/Android可以在页面加载完成后注入数据到window对象
  if (window.nativeInitData) {
    setNativeData(window.nativeInitData);
  }
}

/**
 * 处理原生应用发送的消息
 */
export function handleNativeMessage(message: { type: string; data?: any }): void {
  console.log('Received message from native:', message);
  // toast.info(`Received message from native: ${JSON.stringify(message)}`)
  // 发送自定义事件供组件监听
  const event = new CustomEvent('nativeMessage', { detail: message });
  window.dispatchEvent(event);

  switch (message.type) {
    case 'updateToken':
      if (typeof message.data === 'string') {
        setXToken(message.data);
      }
      break;

    case 'addImage':
      if (typeof message.data === 'string') {
        addImageUrl(message.data);
      }
      break;

    case 'updateImages':
      if (Array.isArray(message.data)) {
        setNativeData({ imageUrls: message.data });
      }
      break;

    case 'clearData':
      clearNativeData();
      break;

    case 'test':
      console.log('Bridge test received from native:', message.data);
      break;

    default:
      console.log('Unknown message type:', message.type);
  }
}

// 扩展Window接口以支持原生应用注入的数据
declare global {
  interface Window {
    nativeBridge?: {
      postMessage: (data: string) => void;
    };
    webkit?: {
      messageHandlers: {
        nativeBridge: {
          postMessage: (data: string) => void;
        };
      };
    };
    nativeInitData?: NativeInitData;
  }
}

// 自动初始化
if (typeof window !== 'undefined') {
  initNativeBridge();
  listenToNativeMessages();

  // 暴露 nativeToWeb 给 ArkTS/iOS/Android 调用
  (window as any).nativeToWeb = {
    // receiveMessage: (message: any) => {
    //   if (typeof message === 'string') {
    //     try {
    //       handleNativeMessage(JSON.parse(message));
    //     } catch (e) {
    //       console.error('Failed to parse message from native:', message);
    //     }
    //   } else {
    //     handleNativeMessage(message);
    //   }
    // },
    toWebMessage: (message: any) => {
      if (typeof message === 'string') {
        try {
          handleNativeMessage(JSON.parse(message));
        } catch (e) {
          console.error('Failed to parse message from native:', message);
        }
      } else {
        handleNativeMessage(message);
      }
    },
    setXToken,
    addImageUrl,
    setNativeData
  };
}
